(function () {
  if (window.__ibCartConversionLoaded) return;
  window.__ibCartConversionLoaded = true;

  var root = document.querySelector('[data-ib-cart-root]');
  if (!root) return;

  var shippingThreshold = 9900;
  var fetchTimeoutMs = 10000;
  var cartReadAttempts = 3;
  var cartMutationAttempts = 3;
  var retryBaseMs = 250;
  var refreshTimer;
  var promotionEligibility = {};
  var promotionEligibilityNode = root.querySelector('[data-ib-promotion-eligibility]');

  if (promotionEligibilityNode) {
    try {
      JSON.parse(promotionEligibilityNode.textContent || '[]').forEach(function (entry) {
        promotionEligibility[String(entry.productId)] = {
          poly: entry.poly === true,
          pin: entry.pin === true,
          vast: entry.vast === true,
          spindown: entry.spindown === true
        };
      });
    } catch (error) {
      promotionEligibility = {};
    }
  }

  function money(cents) {
    var currency = window.Shopify && Shopify.currency && Shopify.currency.active ? Shopify.currency.active : 'USD';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency }).format((cents || 0) / 100);
    } catch (error) {
      return '$' + ((cents || 0) / 100).toFixed(2);
    }
  }

  function fetchWithTimeout(url, options) {
    var controller = typeof window.AbortController === 'function' ? new window.AbortController() : null;
    var timeoutId;
    var requestOptions = options || {};
    if (controller) {
      requestOptions = Object.assign({}, requestOptions, { signal: controller.signal });
      timeoutId = window.setTimeout(function () { controller.abort(); }, fetchTimeoutMs);
      return fetch(url, requestOptions).then(function (response) {
        window.clearTimeout(timeoutId);
        return response;
      }, function (error) {
        window.clearTimeout(timeoutId);
        throw error;
      });
    }
    return Promise.race([
      fetch(url, requestOptions),
      new Promise(function (_, reject) {
        timeoutId = window.setTimeout(function () { reject(new Error('Cart request timed out')); }, fetchTimeoutMs);
      })
    ]).then(function (response) {
      window.clearTimeout(timeoutId);
      return response;
    }, function (error) {
      window.clearTimeout(timeoutId);
      throw error;
    });
  }

  function retryDelay(attemptNumber) {
    var delay = Math.min(retryBaseMs * Math.pow(2, Math.max(attemptNumber - 1, 0)), 1500);
    return new Promise(function (resolve) { window.setTimeout(resolve, delay); });
  }

  function responseError(response, fallbackMessage) {
    return response.json().catch(function () { return {}; }).then(function (body) {
      var error = new Error(body.description || body.message || fallbackMessage);
      error.status = Number(response.status || 0);
      throw error;
    });
  }

  function isTransientRequestError(error) {
    var status = Number(error && error.status || 0);
    if (status === 408 || status === 409 || status === 425 || status === 429 || status >= 500) return true;
    if (!status && error && (error.name === 'AbortError' || error.name === 'TypeError' || /timed out|network|fetch/i.test(String(error.message || '')))) return true;
    return false;
  }

  function getCart() {
    function attempt(attemptNumber) {
      return fetchWithTimeout('/cart.js?t=' + Date.now(), {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' }
      }).then(function (response) {
        if (!response.ok) return responseError(response, 'Cart unavailable');
        return response.json();
      }).catch(function (error) {
        if (attemptNumber >= cartReadAttempts || !isTransientRequestError(error)) throw error;
        return retryDelay(attemptNumber).then(function () { return attempt(attemptNumber + 1); });
      });
    }
    return attempt(1);
  }

  function variantQuantity(cart, variantId) {
    return (cart.items || []).reduce(function (total, item) {
      return Number(item.variant_id) === variantId ? total + Number(item.quantity || 0) : total;
    }, 0);
  }

  function addUpgradeOnce(form) {
    return fetchWithTimeout('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      body: new FormData(form)
    }).then(function (response) {
      if (!response.ok) return responseError(response, 'Unable to add this item.');
      return response.json();
    });
  }

  async function addUpgradeWithConfirmation(form) {
    var variantId = Number(new FormData(form).get('id'));
    var requestedQuantity = Math.max(1, Number(new FormData(form).get('quantity')) || 1);
    var baselineCart = await getCart();
    var targetQuantity = variantQuantity(baselineCart, variantId) + requestedQuantity;
    var lastError;

    for (var attempt = 1; attempt <= cartMutationAttempts; attempt += 1) {
      try {
        return await addUpgradeOnce(form);
      } catch (error) {
        lastError = error;
        if (!isTransientRequestError(error)) throw error;

        /* A timed-out POST may already have committed. Confirm the target
         * quantity before retrying so a conversion-card click cannot double-add. */
        try {
          var currentCart = await getCart();
          if (variantQuantity(currentCart, variantId) >= targetQuantity) return currentCart;
        } catch (readError) {
          if (attempt >= cartMutationAttempts) throw lastError;
        }

        if (attempt >= cartMutationAttempts) throw error;
        await retryDelay(attempt);
      }
    }
    throw lastError;
  }

  function details(item) {
    var eligibility = promotionEligibility[String(item.product_id)] || {};
    var properties = item.properties || {};
    return {
      quantity: Number(item.quantity || 0),
      handle: String(item.handle || ''),
      pin: eligibility.pin === true,
      vast: eligibility.vast === true,
      poly: eligibility.poly === true,
      spindownQualifier: eligibility.spindown === true,
      managedGift: Boolean(properties._gencon_free_spindown)
    };
  }

  function classifyCart(cart) {
    return (cart.items || []).reduce(function (totals, item) {
      var d = details(item);
      totals.handles.push(d.handle);
      if (d.pin) totals.pins += d.quantity;
      if (d.vast) totals.vast += d.quantity;
      if (d.poly) totals.poly += d.quantity;
      if (d.spindownQualifier) totals.spindownQualifiers += d.quantity;
      if (d.managedGift) totals.managedSpindowns += d.quantity;
      return totals;
    }, { pins: 0, vast: 0, poly: 0, spindownQualifiers: 0, managedSpindowns: 0, handles: [] });
  }

  function rewardCopy(totals) {
    var earned = [];
    var polyBundles = Math.floor(totals.poly / 3);
    var pinGroups = Math.floor(totals.pins / 3);
    var vastPairs = Math.floor(totals.vast / 2);
    if (totals.poly) earned.push(totals.poly + ' mystery bonus ' + (totals.poly === 1 ? 'die' : 'dice') + ' earned with qualifying sets.');
    if (polyBundles) earned.push(polyBundles + ' mystery ' + (polyBundles === 1 ? 'slipcase' : 'slipcases') + ' earned. Rewards repeat with every three sets.');
    if (pinGroups) earned.push(pinGroups + ' free ' + (pinGroups === 1 ? 'pin' : 'pins') + ' qualified through Buy 2, Get 1.');
    if (vastPairs) earned.push(vastPairs + ' Vast Grimm BOGO ' + (vastPairs === 1 ? 'pair' : 'pairs') + ' qualified.');
    if (totals.spindownQualifiers) earned.push(totals.spindownQualifiers + ' free Spindown ' + (totals.spindownQualifiers === 1 ? 'd20' : 'd20s') + ' earned.');

    var options = [];
    var polyRemainder = totals.poly % 3;
    var pinRemainder = totals.pins % 3;
    if (totals.poly && polyRemainder) options.push({ needed: 3 - polyRemainder, family: 'poly', title: 'Add ' + (3 - polyRemainder) + ' more qualifying ' + ((3 - polyRemainder) === 1 ? 'set' : 'sets'), body: 'Unlock another mystery slipcase, plus a bonus die with every added set.', href: '/collections/polyhedral-dice-sets', cta: 'Choose qualifying sets' });
    if (totals.pins && pinRemainder) options.push({ needed: 3 - pinRemainder, family: 'pin', title: 'Add ' + (3 - pinRemainder) + ' more eligible ' + ((3 - pinRemainder) === 1 ? 'pin' : 'pins'), body: 'The lowest-priced pin in each group of three is free.', href: '/collections/single-enamel-pins', cta: 'Choose pins' });
    if (totals.vast % 2 === 1) options.push({ needed: 1, family: 'vast', title: 'Add 1 more eligible Vast Grimm item', body: 'The equal- or lower-priced item will be free.', href: '/collections/gen-con-vast-grimm-bogo-eligible', cta: 'Choose Vast Grimm' });
    if (totals.spindownQualifiers > totals.managedSpindowns) options.unshift({ needed: 0, family: 'spindown', title: 'Your free Spindown is being added', body: 'The cart will reconcile automatically before checkout.', href: '/collections/holiday-sale', cta: 'See offer details' });
    options.sort(function (a, b) { return a.needed - b.needed; });
    return { earned: earned, next: options[0] || null };
  }

  function slipcaseCopy(polyCount) {
    var earned = Math.floor(polyCount / 3);
    var progress = polyCount % 3;
    var needed = 3 - progress;
    var status;
    if (earned > 0) {
      status = 'You have ' + earned + ' FREE mystery ' + (earned === 1 ? 'slipcase' : 'slipcases') + '. Add ' + needed + ' more qualifying ' + (needed === 1 ? 'set' : 'sets') + ' to earn another.';
    } else {
      status = 'Add ' + needed + ' qualifying ' + (needed === 1 ? 'set' : 'sets') + ' to earn your first FREE mystery slipcase.';
    }
    return {
      earned: earned,
      progress: progress,
      status: status,
      earnedLabel: earned + ' ' + (earned === 1 ? 'slipcase' : 'slipcases') + ' earned',
      progressLabel: progress + ' of 3 sets toward your next slipcase',
      valueText: progress + ' of 3 qualifying sets toward your next free mystery slipcase; ' + earned + ' earned'
    };
  }

  function render(cart) {
    if (!Number(cart.item_count || 0) && root.querySelector('#CartPageForm')) {
      window.location.reload();
      return;
    }
    var hasUnknownPromotionEligibility = (cart.items || []).some(function (item) {
      return !Object.prototype.hasOwnProperty.call(promotionEligibility, String(item.product_id));
    });
    if (hasUnknownPromotionEligibility) {
      window.location.reload();
      return;
    }
    var totals = classifyCart(cart);
    var remaining = Math.max(0, shippingThreshold - Number(cart.total_price || 0));
    var progress = Math.min(100, Math.round((Number(cart.total_price || 0) / shippingThreshold) * 100));
    var shippingMessage = root.querySelector('[data-ib-shipping-message]');
    var shippingBar = root.querySelector('[data-ib-shipping-progress]');
    var shippingTrack = shippingBar && shippingBar.parentElement;
    if (shippingMessage) shippingMessage.textContent = remaining === 0 ? 'Free U.S. shipping unlocked.' : "You're " + money(remaining) + ' away from free U.S. shipping.';
    if (shippingBar) shippingBar.style.width = progress + '%';
    if (shippingTrack) shippingTrack.setAttribute('aria-valuenow', String(progress));

    var checkout = root.querySelector('[data-ib-checkout]');
    if (checkout) checkout.textContent = 'Checkout securely - ' + money(cart.total_price);
    var savingsRow = root.querySelector('[data-ib-savings-row]');
    var savings = root.querySelector('[data-ib-cart-savings]');
    if (savingsRow) savingsRow.hidden = !cart.total_discount;
    if (savings) savings.textContent = '-' + money(cart.total_discount);

    var copy = rewardCopy(totals);
    var slipcase = slipcaseCopy(totals.poly);
    var slipcaseEarned = root.querySelector('[data-ib-slipcase-earned]');
    var slipcaseStatus = root.querySelector('[data-ib-slipcase-status]');
    var slipcaseMeter = root.querySelector('[data-ib-slipcase-meter]');
    var slipcaseMeterFill = root.querySelector('[data-ib-slipcase-meter-fill]');
    var slipcaseProgressCopy = root.querySelector('[data-ib-slipcase-progress-copy]');
    if (slipcaseEarned) slipcaseEarned.textContent = slipcase.earnedLabel;
    if (slipcaseStatus) slipcaseStatus.textContent = slipcase.status;
    if (slipcaseMeter) {
      slipcaseMeter.setAttribute('aria-valuenow', String(slipcase.progress));
      slipcaseMeter.setAttribute('aria-valuetext', slipcase.valueText);
    }
    if (slipcaseMeterFill) slipcaseMeterFill.style.width = ((slipcase.progress / 3) * 100) + '%';
    if (slipcaseProgressCopy) slipcaseProgressCopy.textContent = slipcase.progressLabel;
    var earnedList = root.querySelector('[data-ib-earned-rewards]');
    if (earnedList) {
      earnedList.innerHTML = copy.earned.map(function (line) { return '<li>' + line + '</li>'; }).join('');
    }
    var next = root.querySelector('[data-ib-next-reward]');
    if (next) {
      var offer = copy.next || { title: 'Every qualifying set earns a bonus die', body: 'Every three qualifying sets also earn a mystery slipcase. Rewards repeat with each multiple of three.', href: '/collections/holiday-sale', cta: 'See every sale deal' };
      next.innerHTML = '<div><strong>' + offer.title + '</strong><span>' + offer.body + '</span></div><a class="btn btn--small" href="' + offer.href + '">' + offer.cta + '</a>';
    }

    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-ib-upgrade-card]'));
    cards.forEach(function (card) {
      var handle = card.getAttribute('data-product-handle');
      var family = card.getAttribute('data-family');
      card.hidden = totals.handles.indexOf(handle) !== -1;
      var rank = 20;
      if (family === 'poly' && totals.poly % 3) rank = 3 - (totals.poly % 3);
      if (family === 'pin' && totals.pins % 3) rank = 3 - (totals.pins % 3);
      if (family === 'vast' && totals.vast % 2) rank = 1;
      card.style.order = String(rank);
    });
  }

  function refresh() {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(function () {
      getCart()
        .then(render)
        .catch(function (error) { console.warn('[Infinite Black cart refresh]', error); });
    }, 180);
  }

  root.addEventListener('submit', function (event) {
    var form = event.target.closest('[data-ib-cart-upgrade]');
    if (!form) return;
    event.preventDefault();
    var button = form.querySelector('button[type="submit"]');
    var status = form.querySelector('[data-ib-upgrade-status]');
    if (button) { button.disabled = true; button.textContent = 'Adding...'; }
    if (status) status.textContent = '';
    addUpgradeWithConfirmation(form)
      .then(function () { window.location.reload(); })
      .catch(function (error) {
        if (status) status.textContent = error.message;
        if (button) { button.disabled = false; button.textContent = 'Add to cart'; }
      });
  });

  var products = root.querySelector('[data-products]');
  if (products && window.MutationObserver) new MutationObserver(refresh).observe(products, { childList: true, subtree: true });
  document.addEventListener('cart:updated', refresh);
  document.addEventListener('ajaxProduct:added', refresh);
  refresh();
})();
